import { c as _c } from "react/compiler-runtime";
import { fetchData } from "@axios/axios-client";
import { FieldType } from "@components/tables/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { diningSileoError, diningSileoInfo } from "@components/drawer/dining/Orders/components/dining-sileo";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { getTenantBackendUrlAndTenant } from "@axios/utils";
import { DINING_ORDER_STAGES, DiningOrder, DiningOrderDetails, initialOrderValues, Order, RestaurantOrdersListResponse } from "../types";
import { uuidV4Regex } from "@app/utils";
import { getCookie } from "@app/actions";
import { seedKnownCreatedOrderIds } from "./seed-created-order-ids";
import { applyDroppedUnsettledResolutions, selectDroppedUnsettledOrders, type DroppedOrderResolution } from "./resolve-dropped-unsettled";
import { downloadCSVContent } from "@components/tables/utils";
import Cookies from "js-cookie";
import { playbackEndTimeoutMs } from "./playback-end-timeout";

/** ponytail: unsettled queue only — drop pre-cutoff staging noise; remove when full history is needed. */
const UNSETTLED_ORDERS_CREATED_AT_START = "2026-08-05T00:00:00";

// Types
interface OrderData {
  data: DiningOrder[];
  total: number;
}
interface UseOrdersParams {
  sort?: string;
  sortOrder?: string;
  lim?: number;
  filterFields: FieldType[];
  pageNum?: number;
}
interface UpdateOrderStageParams {
  orderId: string;
  stage: string;
  cancellationReason?: string;
}
const ORDER_AUDIO_UNLOCK_SESSION_KEY = "dining-order-audio-unlocked";
class AudioManager {
  private static instance: AudioManager;
  private bellAudio: HTMLAudioElement | null = null;
  private cancelAudio: HTMLAudioElement | null = null;
  /** Serializes starts so N orders → N distinct rings (not one overlapping mush). */
  private playbackQueue: Promise<void> = Promise.resolve();
  /** Browsers block play() until a user gesture unlocks the document audio context. */
  private unlocked = false;
  private gestureUnlockInstalled = false;
  private constructor() {}
  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * Call from a user gesture so later socket plays are allowed.
   * `audible: false` for background gesture unlock (nav clicks) — tiny volume so
   * kitchens don't hear a chirp every time they open a sidebar link.
   */
  async unlock(options?: {
    audible?: boolean;
  }): Promise<boolean> {
    if (this.unlocked) return true;
    if (typeof window === "undefined") return false;
    try {
      const warm = new Audio("/audio/ringRing.mp3");
      // Audible chirp under the explicit Enable click — muted play alone is not
      // enough on some browsers; nav gestures use near-silent volume instead.
      warm.volume = options?.audible === false ? 0.01 : 0.35;
      await warm.play();
      warm.pause();
      try {
        warm.currentTime = 0;
      } catch {
        // ignore
      }
      this.unlocked = true;
      try {
        sessionStorage.setItem(ORDER_AUDIO_UNLOCK_SESSION_KEY, "1");
      } catch {
        // private mode / blocked storage
      }
      this.preloadCancel();
      return true;
    } catch {
      return false;
    }
  }
  isUnlocked(): boolean {
    return this.unlocked;
  }

  /** True if unlock succeeded earlier this tab (memory or sessionStorage). */
  wasUnlockedThisSession(): boolean {
    if (this.unlocked) return true;
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(ORDER_AUDIO_UNLOCK_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  }

  /**
   * One-shot capture-phase listeners so any click/key in the admin unlocks
   * autoplay before the kitchen lands on /orders (skips the blocking gate).
   */
  installGestureUnlock(): void {
    if (typeof window === "undefined" || this.gestureUnlockInstalled) return;
    this.gestureUnlockInstalled = true;
    const onGesture = () => {
      void this.unlock({
        audible: false
      }).then(ok => {
        if (!ok) return;
        window.removeEventListener("pointerdown", onGesture, true);
        window.removeEventListener("keydown", onGesture, true);
      });
    };
    window.addEventListener("pointerdown", onGesture, true);
    window.addEventListener("keydown", onGesture, true);
  }
  playBell(): Promise<void> {
    const playback = this.playbackQueue.then(() => this.playFile("/audio/ringRing.mp3"));
    this.playbackQueue = playback.catch(() => undefined);
    return playback;
  }

  /** Distinct cue for cancelled orders (softer tone vs new-order ring). */
  playCancel(): Promise<void> {
    const playback = this.playbackQueue.then(() => this.playFile("/audio/cancel.wav"));
    this.playbackQueue = playback.catch(() => undefined);
    return playback;
  }
  private getOrCreatePreload(kind: "bell" | "cancel", src: string): HTMLAudioElement {
    if (kind === "bell") {
      if (!this.bellAudio) {
        this.bellAudio = new Audio(src);
        this.bellAudio.preload = "auto";
      }
      return this.bellAudio;
    }
    if (!this.cancelAudio) {
      this.cancelAudio = new Audio(src);
      this.cancelAudio.preload = "auto";
    }
    return this.cancelAudio;
  }
  private async ensureAudioReady(audio: HTMLAudioElement): Promise<void> {
    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;
    await new Promise<void>(resolve => {
      const finish = () => {
        cleanup();
        resolve();
      };
      const cleanup = () => {
        audio.removeEventListener("canplaythrough", finish);
        audio.removeEventListener("loadeddata", finish);
        audio.removeEventListener("error", finish);
      };
      audio.addEventListener("canplaythrough", finish);
      audio.addEventListener("loadeddata", finish);
      audio.addEventListener("error", finish);
      // Never hang the serial queue if metadata never arrives.
      setTimeout(finish, 1500);
      audio.load();
    });
  }
  private async playFile(src: string): Promise<void> {
    try {
      // Fresh element per play — shared elements + a missed `ended` used to
      // leave the queue hung so only the first of N orders rang.
      const audio = new Audio(src);
      audio.preload = "auto";
      await this.ensureAudioReady(audio);
      try {
        audio.currentTime = 0;
      } catch {
        // Ignore until metadata is ready; play() still works.
      }
      await audio.play();
      const timeoutMs = playbackEndTimeoutMs(audio.duration);
      await Promise.race([new Promise<void>(resolve => {
        if (audio.ended) {
          resolve();
          return;
        }
        const finish = () => {
          audio.removeEventListener("ended", finish);
          audio.removeEventListener("error", finish);
          resolve();
        };
        audio.addEventListener("ended", finish);
        audio.addEventListener("error", finish);
      }), new Promise<void>(resolve => {
        setTimeout(resolve, timeoutMs);
      })]);
    } catch (error) {
      console.warn("Failed to play notification sound:", error);
    }
  }

  /** Warm clips so the first socket event isn't blocked on load. */
  preloadCancel(): void {
    try {
      this.getOrCreatePreload("cancel", "/audio/cancel.wav").load();
      this.getOrCreatePreload("bell", "/audio/ringRing.mp3").load();
    } catch {
      // ignore preload failures
    }
  }
  cleanup(): void {
    this.bellAudio?.pause();
    this.cancelAudio?.pause();
    if (this.bellAudio) this.bellAudio.src = "";
    if (this.cancelAudio) this.cancelAudio.src = "";
    this.bellAudio = null;
    this.cancelAudio = null;
    this.playbackQueue = Promise.resolve();
  }
}
const audioManager = AudioManager.getInstance();

/** Unlock autoplay after a user gesture on the orders page. */
export const unlockOrderNotificationAudio = (options?: {
  audible?: boolean;
}) => audioManager.unlock(options);
export const isOrderNotificationAudioUnlocked = () => audioManager.isUnlocked();
export const wasOrderNotificationAudioUnlockedThisSession = () => audioManager.wasUnlockedThisSession();
/** Install once (branch shell) so nav clicks unlock before /orders mounts. */
export const installOrderNotificationGestureUnlock = () => audioManager.installGestureUnlock();
/** Local cancel cue (admin cancel has no socket emit). */
export const playOrderCancelSound = () => audioManager.playCancel();
export const playOrderBellSound = () => audioManager.playBell();
export const unsettledOrderStages = DINING_ORDER_STAGES.filter(stage => stage !== "delivered" && stage !== "cancelled");
const toQueueOrder = (item: DiningOrder, existing?: Order): Order => ({
  ...initialOrderValues,
  ...item,
  member: {
    ...initialOrderValues.member,
    ...item.member,
    profileImage: existing?.member?.profileImage ?? null
  },
  restaurant: existing?.restaurant ?? initialOrderValues.restaurant,
  waiter: existing?.waiter ?? null,
  zone: existing?.zone ?? null,
  subzone: existing?.subzone ?? null,
  orderedProducts: existing?.orderedProducts ?? []
});
export const useOrders = ({
  sort,
  sortOrder,
  lim,
  filterFields,
  pageNum
}: UseOrdersParams) => {
  const params = useParams();
  const [ordersLength, setOrdersLength] = useState(0);

  // Memoize filter fields to prevent unnecessary recalculations
  const memoizedFilterFields = useMemo(() => filterFields, [filterFields]);

  // Build query parameters
  const queryParams = useMemo(() => {
    const qp: Record<string, any> = {
      limit: lim,
      page: pageNum,
      ...(sort && {
        sort
      }),
      ...(sortOrder && {
        sortOrder
      })
    };
    memoizedFilterFields.forEach(field => {
      if (field.fieldValue && field.fieldValue.length > 0) {
        switch (field.fieldName) {
          case "createdAt":
            if (field.fieldValue[0] && field.fieldValue[1]) {
              qp.createdAtStart = `${field.fieldValue[0]}T00:00:00`;
              qp.createdAtEnd = `${field.fieldValue[1]}T23:59:59`;
            }
            break;
          default:
            qp[field.fieldName] = field.fieldValue;
        }
      }
    });
    return qp;
  }, [lim, pageNum, sort, sortOrder, memoizedFilterFields]);
  const stringifiedQueryParams = useMemo(() => JSON.stringify(queryParams), [queryParams]);
  const {
    data: ordersData,
    refetch,
    isFetching,
    error: error_0
  } = useQuery({
    queryKey: ["restaurantOrders", stringifiedQueryParams, params.restaurantId],
    queryFn: async (): Promise<OrderData> => {
      try {
        const res = (await fetchData(queryParams, `/dining/restaurants/${params.restaurantId}/orders`, "GET", true)) as RestaurantOrdersListResponse;
        if (ordersLength <= 0) {
          setOrdersLength(res.diningOrders?.length || 0);
        }
        return {
          data: res.diningOrders ?? [],
          total: res.pagination?.totalCount ?? 0
        };
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        throw error;
      }
    },
    placeholderData: previousData => previousData
  });
  const exportAll = async () => {
    try {
      const qp_0 = {
        ...queryParams
      };
      delete qp_0.limit;
      delete qp_0.page;
      const res_0 = await fetchData({
        ...qp_0,
        limit: ordersData?.total || 10_000
      }, `/dining/restaurants/${params.restaurantId}/orders/export`, "GET", true);
      if (res_0) {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        downloadCSVContent(res_0, `orders-${timestamp}`);
      }
    } catch (e) {
      console.error("Error exporting orders:", e);
    }
  };
  return {
    orders: ordersData?.data || [],
    fetchOrders: refetch,
    ordersLength,
    total: ordersData?.total || 0,
    isLoading: isFetching,
    exportAll,
    error: error_0
  };
};
export const useLatestOrders = (restaurantId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["latestOrder", restaurantId],
    enabled,
    queryFn: async (): Promise<DiningOrder[]> => {
      try {
        const res = (await fetchData({
          limit: 5,
          sort: "createdAt",
          sortOrder: "desc"
        }, `/dining/restaurants/${restaurantId}/orders`, "GET", true)) as RestaurantOrdersListResponse;
        return res.diningOrders ?? [];
      } catch (error) {
        console.error("Failed to fetch latest orders:", error);
        throw error;
      }
    }
  });
};
export const useUnsettledOrdersCount = () => {
  const $ = _c(8);
  const params = useParams();
  const restaurantId = params.restaurantId as string;
  let t0;
  if ($[0] !== restaurantId) {
    t0 = ["unsettledOrdersCount", restaurantId];
    $[0] = restaurantId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!restaurantId;
  let t2;
  if ($[2] !== restaurantId) {
    t2 = async () => {
      const res = (await fetchData({
        limit: 1,
        page: 1,
        stage: unsettledOrderStages,
        createdAtStart: UNSETTLED_ORDERS_CREATED_AT_START
      }, `/dining/restaurants/${restaurantId}/orders`, "GET", true)) as RestaurantOrdersListResponse;
      return res.pagination?.totalCount ?? 0;
    };
    $[2] = restaurantId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      enabled: t1,
      queryFn: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useOrder = orderId => {
  const $ = _c(11);
  const params = useParams();
  let t0;
  if ($[0] !== orderId) {
    t0 = ["order", orderId];
    $[0] = orderId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== orderId || $[3] !== params) {
    t1 = async () => {
      ;
      try {
        return (await fetchData({}, `/dining/restaurants/${params.restaurantId}/orders/${orderId}`, "GET", true)) as DiningOrderDetails;
      } catch (t2) {
        const error = t2;
        console.error(`Failed to fetch order ${orderId}:`, error);
        throw error;
      }
    };
    $[2] = orderId;
    $[3] = params;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== orderId) {
    t2 = !!orderId && uuidV4Regex.test(orderId);
    $[5] = orderId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQuery(t3);
};
export const useUpdateOrderStage = () => {
  const queryClient = useQueryClient();
  const params = useParams();
  return useMutation({
    mutationFn: async ({
      orderId,
      stage,
      cancellationReason
    }: UpdateOrderStageParams) => {
      try {
        const res = await fetchData({
          stage,
          cancellationReason
        }, stage === "cancelled" ? `/dining/restaurants/${params.restaurantId}/orders/${orderId}?skipThirdParty=true` : `/dining/restaurants/${params.restaurantId}/orders/${orderId}`, "PATCH");
        return res;
      } catch (error) {
        console.error(`Failed to update order ${orderId}:`, error);
        throw error;
      }
    },
    onMutate: async ({
      orderId: orderId_0,
      stage: stage_0
    }) => {
      // Cancel outgoing refetches
      await Promise.all([queryClient.cancelQueries({
        queryKey: ["order", orderId_0]
      }), queryClient.cancelQueries({
        queryKey: ["restaurantOrders"]
      }), queryClient.cancelQueries({
        queryKey: ["latestOrder"]
      })]);

      // Snapshot previous values for rollback
      const previousOrder = queryClient.getQueryData(["order", orderId_0]);
      const previousOrders = queryClient.getQueriesData({
        queryKey: ["restaurantOrders"]
      });
      const previousLatestOrders = queryClient.getQueriesData({
        queryKey: ["latestOrder"]
      });

      // Optimistic updates
      queryClient.setQueryData(["order", orderId_0], (old: any) => old ? {
        ...old,
        stage: stage_0
      } : old);
      queryClient.setQueriesData({
        queryKey: ["restaurantOrders"]
      }, (old_0: OrderData | undefined) => {
        if (!old_0?.data) return old_0;
        return {
          ...old_0,
          data: old_0.data.map(order => order.id === orderId_0 ? {
            ...order,
            stage: stage_0
          } : order)
        };
      });
      queryClient.setQueriesData({
        queryKey: ["latestOrder"]
      }, (old_1: DiningOrder[] | undefined) => {
        if (!Array.isArray(old_1)) return old_1;
        return old_1.map(order_0 => order_0.id === orderId_0 ? {
          ...order_0,
          stage: stage_0
        } : order_0);
      });
      return {
        previousOrder,
        previousOrders,
        previousLatestOrders
      };
    },
    onError: (error_0, {
      orderId: orderId_1
    }, context) => {
      // Rollback optimistic updates on error
      if (context?.previousOrder) {
        queryClient.setQueryData(["order", orderId_1], context.previousOrder);
      }
      if (context?.previousOrders) {
        context.previousOrders.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousLatestOrders) {
        context.previousLatestOrders.forEach(([queryKey_0, data_0]) => {
          queryClient.setQueryData(queryKey_0, data_0);
        });
      }
      diningSileoError({
        id: "dining-update-stage-error",
        title: "Failed to update order status. Please try again."
      });
    },
    onSettled: () => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ["restaurantOrders"]
      });
      queryClient.invalidateQueries({
        queryKey: ["latestOrder"]
      });
      queryClient.invalidateQueries({
        queryKey: ["unsettledOrdersCount"]
      });
    }
  });
};
export const useWebSocketSubscription = ({
  setOrdersQueue
}: {
  setOrdersQueue: Dispatch<SetStateAction<Order[]>>;
}) => {
  const socketInitialized = useRef(false);
  const queryClient = useQueryClient();
  const params = useParams();
  const restaurantId = params.restaurantId as string;
  const [orderId, setOrderId] = useState<string>("");
  const {
    data: order,
    isLoading: isOrderLoading
  } = useOrder(orderId);
  // After first unsettled hydrate: ids we've already considered (ring or silent seed).
  const knownCreatedOrderIdsRef = useRef<Set<string>>(new Set());
  const hasSeededBoardRef = useRef(false);
  // Socket creates before first hydrate — ring after seed (incl. ids already in the list).
  const pendingCreatedOrderIdsRef = useRef<Set<string>>(new Set());
  // Ids observed in at least one unsettled GET — used so poll/socket lag after
  // create doesn't treat a still-enqueueing card as a remote cancel.
  const lastSeenInUnsettledApiRef = useRef<Set<string>>(new Set());
  // Bumped on close so in-flight list/detail fetches can't re-fill the queue
  // and trip the forceClose auto-open path.
  const queueEpochRef = useRef(0);
  const newOrdersToastRef = useRef<{
    count: number;
    id?: string;
    timer?: ReturnType<typeof setTimeout>;
  }>({
    count: 0
  });
  const announceCreatedOrder = useCallback((nextOrderId: string) => {
    if (!nextOrderId) return false;

    // Before first unsettled hydrate: hold for post-seed announce (don't ring yet —
    // reconnect may replay order.created for board rows).
    if (!hasSeededBoardRef.current) {
      pendingCreatedOrderIdsRef.current.add(nextOrderId);
      return false;
    }
    if (knownCreatedOrderIdsRef.current.has(nextOrderId)) return false;
    knownCreatedOrderIdsRef.current.add(nextOrderId);
    void audioManager.playBell();
    const toastState = newOrdersToastRef.current;
    toastState.count += 1;
    const resetNewOrdersToastCount = () => {
      toastState.count = 0;
      toastState.id = undefined;
      if (toastState.timer) {
        clearTimeout(toastState.timer);
        toastState.timer = undefined;
      }
    };
    toastState.id = diningSileoInfo({
      id: "dining-new-unsettled-orders",
      title: `${toastState.count} new ${toastState.count === 1 ? "order" : "orders"}`,
      duration: 30_000
    });
    if (toastState.timer) clearTimeout(toastState.timer);
    toastState.timer = setTimeout(resetNewOrdersToastCount, 30_000);
    return true;
  }, []);
  const fetchOrder = (nextOrderId_0: string) => {
    // Only touch this order's query. Invalidating ["order"] (prefix) also
    // refetches the previously selected order — looks like 2 fetches on switch.
    setOrderId(nextOrderId_0);
    void queryClient.invalidateQueries({
      queryKey: ["order", nextOrderId_0]
    });
  };
  const handleOrderClose = () => {
    // Invalidate in-flight loadUnsettledOrders / ensureOrderDetails so they
    // can't re-fill the queue after clear and auto-reopen the drawer.
    queueEpochRef.current += 1;
    setOrderId("");
    setOrdersQueue([]);
    // Keep known + hasSeededBoard — clearing them re-enters first-hydrate and
    // used to drop the ring for the order that reopens the drawer.
  };
  const ensureOrderDetails = useCallback(async (nextOrderId_1: string) => {
    if (!nextOrderId_1 || !uuidV4Regex.test(nextOrderId_1) || !restaurantId) {
      return;
    }
    const epoch = queueEpochRef.current;
    try {
      const fetched = await queryClient.fetchQuery({
        queryKey: ["order", nextOrderId_1],
        queryFn: async () => (await fetchData({}, `/dining/restaurants/${restaurantId}/orders/${nextOrderId_1}`, "GET", true)) as DiningOrderDetails
      });
      if (epoch !== queueEpochRef.current) return;
      setOrdersQueue(prevOrders => {
        const safePrevOrders = Array.isArray(prevOrders) ? prevOrders : [];
        const hasOrder = safePrevOrders.some(queuedOrder => queuedOrder.id === fetched.id);
        const isUnsettled = (unsettledOrderStages as readonly string[]).includes(fetched.stage);
        if (!hasOrder) {
          // Don't inject settled details into the unsettled sidebar.
          return isUnsettled ? [fetched, ...safePrevOrders] : safePrevOrders;
        }
        if (!isUnsettled && fetched.stage !== "cancelled") {
          return safePrevOrders.filter(queuedOrder_0 => queuedOrder_0.id !== fetched.id);
        }
        return safePrevOrders.map(queuedOrder_1 => queuedOrder_1.id === fetched.id ? fetched : queuedOrder_1);
      });
    } catch (error) {
      console.error(`Failed to fetch order ${nextOrderId_1}:`, error);
    }
  }, [queryClient, restaurantId, setOrdersQueue]);
  const loadUnsettledOrders = useCallback(async () => {
    if (!restaurantId) return;
    const epoch_0 = queueEpochRef.current;
    try {
      const res = (await fetchData({
        limit: 100,
        page: 1,
        stage: unsettledOrderStages,
        sort: "createdAt",
        sortOrder: "desc",
        createdAtStart: UNSETTLED_ORDERS_CREATED_AT_START
      }, `/dining/restaurants/${restaurantId}/orders`, "GET", true)) as RestaurantOrdersListResponse;
      if (epoch_0 !== queueEpochRef.current) return;
      const list = res.diningOrders ?? [];
      const listIds = list.map(item => item.id);
      if (!hasSeededBoardRef.current) {
        // First load: seed board silently; ring for live socket creates held in pending
        // (including ids already in the list — e.g. create during this fetch).
        const pending = [...pendingCreatedOrderIdsRef.current];
        pendingCreatedOrderIdsRef.current.clear();
        const {
          known,
          toAnnounce
        } = seedKnownCreatedOrderIds(listIds, pending);
        knownCreatedOrderIdsRef.current = known;
        hasSeededBoardRef.current = true;
        for (const id of toAnnounce) {
          announceCreatedOrder(id);
        }
      } else {
        // Later refreshes (incl. after a socket): ring for each newly seen id.
        // Covers missed order.created events when another emit triggers a refresh.
        for (const id_0 of listIds) {
          announceCreatedOrder(id_0);
        }
      }
      const floorMs = Date.parse(UNSETTLED_ORDERS_CREATED_AT_START);
      for (const id_1 of listIds) {
        lastSeenInUnsettledApiRef.current.add(id_1);
      }
      let droppedIds: string[] = [];
      setOrdersQueue(prevOrders_0 => {
        const prev = Array.isArray(prevOrders_0) ? prevOrders_0 : [];
        const existingById = new Map(prev.map(order_0 => [order_0.id, order_0]));
        const active = list.map(item_0 => toQueueOrder(item_0, existingById.get(item_0.id)));
        const activeIds = new Set(active.map(order_1 => order_1.id));
        // Keep cancelled cards in the open drawer queue so the count doesn't drop
        // and admins still see a cancelled-since indicator.
        const retainedCancelled = prev.filter(order_2 => order_2.stage === "cancelled" && !activeIds.has(order_2.id) && (!order_2.createdAt || Date.parse(order_2.createdAt) >= floorMs));
        // Ambiguous drops (cancel and deliver both leave unsettled) — keep the
        // prior card until detail stage resolves; do not assume cancelled.
        const dropped = selectDroppedUnsettledOrders({
          prev,
          activeIds,
          previouslySeenIds: lastSeenInUnsettledApiRef.current,
          createdAtFloorMs: floorMs
        });
        droppedIds = dropped.map(order_3 => order_3.id);
        return [...active, ...retainedCancelled, ...dropped];
      });
      if (droppedIds.length > 0) {
        void (async () => {
          const resolutions = (await Promise.all(droppedIds.map(async (id_2): Promise<DroppedOrderResolution | null> => {
            if (!uuidV4Regex.test(id_2)) return null;
            try {
              const fetched_0 = await queryClient.fetchQuery({
                queryKey: ["order", id_2],
                queryFn: async () => (await fetchData({}, `/dining/restaurants/${restaurantId}/orders/${id_2}`, "GET", true)) as DiningOrderDetails
              });
              return {
                id: fetched_0.id,
                stage: fetched_0.stage,
                updatedAt: fetched_0.updatedAt
              };
            } catch (error_1) {
              console.error(`Failed to resolve dropped unsettled order ${id_2}:`, error_1);
              return null;
            }
          }))).filter((r): r is DroppedOrderResolution => r != null);
          if (epoch_0 !== queueEpochRef.current || resolutions.length === 0) {
            return;
          }
          const cancelledAt = new Date().toISOString();
          let newlyCancelledCount = 0;
          setOrdersQueue(prevOrders_1 => {
            const prev_0 = Array.isArray(prevOrders_1) ? prevOrders_1 : [];
            const applied = applyDroppedUnsettledResolutions({
              prev: prev_0,
              resolutions,
              unsettledStages: unsettledOrderStages,
              cancelledAt
            });
            newlyCancelledCount = applied.newlyCancelledCount;
            return applied.next;
          });
          if (newlyCancelledCount > 0) {
            void audioManager.playCancel();
          }
        })();
      }
    } catch (error_0) {
      console.error("Failed to load unsettled orders:", error_0);
    }
  }, [announceCreatedOrder, queryClient, restaurantId, setOrdersQueue]);
  const loadUnsettledOrdersRef = useRef(loadUnsettledOrders);
  loadUnsettledOrdersRef.current = loadUnsettledOrders;

  // ponytail: multi-task ECS only emits to sockets on the creating task; poll so
  // every open orders page still rings/refreshes. 15s keeps API load ~4/min/tab.
  // Drop when Redis adapter ships.
  useEffect(() => {
    if (!restaurantId) return;
    const poll = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void loadUnsettledOrdersRef.current();
    };
    const intervalId = setInterval(poll, 15_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [restaurantId]);
  useEffect(() => {
    // orderId cleared on close — don't re-inject cached details into an empty queue.
    if (!order || !orderId) return;
    setOrdersQueue(prevOrders_2 => {
      const safePrevOrders_0 = Array.isArray(prevOrders_2) ? prevOrders_2 : [];
      const hasOrder_0 = safePrevOrders_0.some(queuedOrder_2 => queuedOrder_2.id === order.id);
      if (hasOrder_0) {
        return safePrevOrders_0.map(queuedOrder_3 => queuedOrder_3.id === order.id ? order : queuedOrder_3);
      }

      // Don't inject settled orders into the unsettled sidebar when viewing from the table.
      if (!(unsettledOrderStages as readonly string[]).includes(order.stage)) {
        return safePrevOrders_0;
      }
      return [order, ...safePrevOrders_0];
    });
  }, [order, orderId, setOrdersQueue]);
  useEffect(() => {
    if (socketInitialized.current || !restaurantId) return;
    socketInitialized.current = true;
    const subdomain = typeof window !== "undefined" ? window.location.hostname : "";
    const {
      url
    } = getTenantBackendUrlAndTenant(subdomain);
    const socket = io(`${url}/dining-orders`, {
      path: "/api/v2/socket.io",
      transports: ["websocket"],
      auth: {
        token: localStorage.getItem("access_token") || Cookies.get("access_token")
      },
      // Restaurant page only joins the restaurant room. Also joining `branch`
      // double-delivers order.created (gateway emits to both rooms).
      query: {
        restaurantId
      }
    });

    // Dedupe identical socket payloads (same orderId within a short window).
    const recentEventKeys = new Map<string, number>();
    const claimSocketEvent = (kind: string, orderId_0: string) => {
      if (!orderId_0) return false;
      const key = `${kind}:${orderId_0}`;
      const now = Date.now();
      const prev_1 = recentEventKeys.get(key);
      if (prev_1 != null && now - prev_1 < 2500) return false;
      recentEventKeys.set(key, now);
      // Bound map size for long-lived pages.
      if (recentEventKeys.size > 200) {
        for (const [k, ts] of recentEventKeys) {
          if (now - ts > 60_000) recentEventKeys.delete(k);
        }
      }
      return true;
    };

    // ponytail: fetch each emit independently — throttle + single orderId state dropped concurrent orders
    const fetchAndEnqueueOrder = async (id_3: string) => {
      if (!id_3 || !uuidV4Regex.test(id_3)) return;
      try {
        const fetched_1 = await queryClient.fetchQuery({
          queryKey: ["order", id_3],
          queryFn: async () => (await fetchData({}, `/dining/restaurants/${restaurantId}/orders/${id_3}`, "GET", true)) as DiningOrderDetails
        });
        setOrdersQueue(prevOrders_3 => {
          const safePrevOrders_1 = Array.isArray(prevOrders_3) ? prevOrders_3 : [];
          const hasOrder_1 = safePrevOrders_1.some(queuedOrder_4 => queuedOrder_4.id === fetched_1.id);
          const isUnsettled_0 = (unsettledOrderStages as readonly string[]).includes(fetched_1.stage);

          // Newest first — matches loadUnsettledOrders sort (createdAt desc).
          // Settled (delivered) must not re-enter the unsettled sidebar.
          if (!hasOrder_1) {
            return isUnsettled_0 ? [fetched_1, ...safePrevOrders_1] : safePrevOrders_1;
          }
          if (!isUnsettled_0 && fetched_1.stage !== "cancelled") {
            return safePrevOrders_1.filter(queuedOrder_5 => queuedOrder_5.id !== fetched_1.id);
          }
          return safePrevOrders_1.map(queuedOrder_6 => queuedOrder_6.id === fetched_1.id ? fetched_1 : queuedOrder_6);
        });
      } catch (error_2) {
        console.error(`Failed to fetch order ${id_3}:`, error_2);
      }
    };
    const refreshQueueAfterSocketEvent = async (orderId_1?: string) => {
      if (orderId_1) {
        await fetchAndEnqueueOrder(orderId_1);
      }
      // Authoritative refresh so the left list stays sorted and complete even if
      // the single-order fetch fails or races with create.
      await loadUnsettledOrdersRef.current();
    };
    const handleNewOrder = (payload: {
      orderId?: string;
      id?: string;
    }) => {
      const createdOrderId = payload?.orderId || payload?.id || "";
      if (!claimSocketEvent("created", createdOrderId)) return;
      announceCreatedOrder(createdOrderId);
      queryClient.invalidateQueries({
        queryKey: ["restaurantOrders"],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ["unsettledOrdersCount"]
      });
      void refreshQueueAfterSocketEvent(createdOrderId);
    };
    const handleDeliveryUpdate = (payload_0: {
      orderId?: string;
      id?: string;
    }) => {
      const orderId_2 = payload_0?.orderId || payload_0?.id || "";
      if (!claimSocketEvent("delivery-update", orderId_2)) return;
      void audioManager.playBell();
      diningSileoInfo({
        id: "dining-delivery-updated",
        title: "Order delivery updated"
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurantOrders"]
      });
      queryClient.invalidateQueries({
        queryKey: ["unsettledOrdersCount"]
      });
      queryClient.invalidateQueries({
        queryKey: ["order"],
        exact: false
      });
      void refreshQueueAfterSocketEvent(orderId_2);
    };
    const handleOrderCancelled = (payload_1: {
      orderId: string;
    }) => {
      if (!claimSocketEvent("cancelled", payload_1.orderId)) return;
      void audioManager.playCancel();
      queryClient.invalidateQueries({
        queryKey: ["restaurantOrders"],
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: ["unsettledOrdersCount"]
      });
      // Keep the card in the queue with a cancelled marker — do not reload unsettled
      // (API omits cancelled and would drop it from the list/count).
      setOrdersQueue(prev_2 => {
        const safePrev = Array.isArray(prev_2) ? prev_2 : [];
        if (!safePrev.some(queuedOrder_7 => queuedOrder_7.id === payload_1.orderId)) {
          return safePrev;
        }
        const cancelledAt_0 = new Date().toISOString();
        return safePrev.map(queuedOrder_8 => queuedOrder_8.id === payload_1.orderId ? {
          ...queuedOrder_8,
          stage: "cancelled",
          updatedAt: cancelledAt_0
        } : queuedOrder_8);
      });
    };
    socket.on("connect", () => {
      console.info("WebSocket connected");
      socketInitialized.current = true;
      // Warm cancel clip so the first order.cancelled isn't blocked on load.
      audioManager.preloadCancel();
    });
    socket.off("connect_error");
    socket.off("order.created");
    socket.off("order.delivery-update");
    socket.off("order.cancelled");
    socket.on("order.created", handleNewOrder);
    socket.on("order.delivery-update", handleDeliveryUpdate);
    socket.on("order.cancelled", handleOrderCancelled);
    socket.on("connect_error", (error_3: any) => {
      console.error(error_3, "connect_error");
      socketInitialized.current = false;
    });
    return () => {
      if (newOrdersToastRef.current.timer) {
        clearTimeout(newOrdersToastRef.current.timer);
      }
      socket.off();
      socket.disconnect();
      socketInitialized.current = false;
    };
  }, [announceCreatedOrder, queryClient, restaurantId, setOrdersQueue]);
  return {
    isOrderLoading,
    /** Hydrated details for the current `orderId` (incl. cancelled/settled). */
    order,
    fetchOrder,
    handleOrderClose,
    loadUnsettledOrders,
    ensureOrderDetails
  };
};